import { useEffect, useRef } from 'react';
import { create } from 'zustand';

import type { StudioCapabilityName } from '../../../engine/scripting/capabilityNames';
import { computeHollowScriptMarkers, type HollowScriptMarker } from '../../../engine/scripting/monaco/diagnostics';
import { buildScriptModules, scriptModulePath } from '../../../engine/scripting/scriptModules';
import { useScriptStore } from '../../scripting/scriptStore';
import type { ScriptDocument } from '../../services/scripts';

// Every script's live diagnostics, for the whole roster, not only the file
// on screen. Monaco's own markers (HollowScriptFileEditor) cover the open
// model; this store is what lets the Problems panel list an error in a
// script that is not open, the Scripts list show a red count beside it,
// and the editor's status bar count errors the way Godot's status bar and
// Roblox's Script Analysis window do. Checked against the same real
// `check()` and the same project capability grant as the editor's inline
// squiggles, so the two can never disagree.

export interface ScriptProblem extends HollowScriptMarker {
  readonly scriptId: string;
  readonly scriptName: string;
}

interface ScriptProblemsState {
  readonly problems: readonly ScriptProblem[];
  readonly byScript: Readonly<Record<string, readonly ScriptProblem[]>>;
  recompute: (scripts: readonly ScriptDocument[], capabilities: ReadonlySet<StudioCapabilityName> | undefined) => Promise<void>;
  clear: () => void;
}

const RECHECK_DEBOUNCE_MS = 350;

/** Guards `recompute` the same way `monacoAdapter.ts`'s own
 * `markerCallGeneration` guards `updateHollowScriptMarkers`: `check` is
 * async now (the native tooling wasm loads lazily), so a slower, older
 * `recompute` call must not overwrite a newer one's result once it
 * resolves. One counter for the whole store, not per script: `recompute`
 * always replaces the *entire* roster's problems in one `set`, never one
 * script's alone, so a stale whole-roster result is exactly what this
 * guards against. */
let recomputeGeneration = 0;

export const useScriptProblemsStore = create<ScriptProblemsState>((set) => ({
  problems: [],
  byScript: {},

  recompute: async (scripts, capabilities) => {
    const generation = ++recomputeGeneration;
    const problems: ScriptProblem[] = [];
    const byScript: Record<string, ScriptProblem[]> = {};
    for (const script of scripts) {
      // The same sibling map a real run resolves imports against, so an
      // `import` is a problem here exactly when it would be at run time.
      const markers = await computeHollowScriptMarkers(script.source, capabilities, scriptModulePath(script.name), {
        modules: buildScriptModules(scripts, script.name),
      });
      const own = markers.map((marker): ScriptProblem => ({ ...marker, scriptId: script.id, scriptName: script.name }));
      byScript[script.id] = own;
      problems.push(...own);
    }
    if (generation !== recomputeGeneration) return;
    set({ problems, byScript });
  },

  clear: () => set({ problems: [], byScript: {} }),
}));

export interface ProblemCounts {
  readonly errors: number;
  readonly warnings: number;
}

export function countProblems(problems: readonly HollowScriptMarker[] | undefined): ProblemCounts {
  let errors = 0;
  let warnings = 0;
  for (const problem of problems ?? []) {
    if (problem.severity === 'error') errors += 1;
    else warnings += 1;
  }
  return { errors, warnings };
}

/**
 * Keeps `useScriptProblemsStore` current against the roster and the
 * project's capability grant, debounced so a burst of keystrokes costs one
 * check of the roster, not one per key. Mount once per Code workspace
 * (the editor panel does); every other panel just reads the store.
 */
export function useScriptProblemsSync(capabilities: ReadonlySet<StudioCapabilityName> | undefined): void {
  const scripts = useScriptStore((s) => s.scripts);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => {
    if (timer.current !== undefined) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = undefined;
      void useScriptProblemsStore.getState().recompute(scripts, capabilities);
    }, RECHECK_DEBOUNCE_MS);
    return () => {
      if (timer.current !== undefined) clearTimeout(timer.current);
    };
  }, [scripts, capabilities]);
  useEffect(() => () => useScriptProblemsStore.getState().clear(), []);
}
